import { useState, useEffect, useRef } from "react";
import axios from "axios";
import {
  BarChart3, Target, Zap, FileText, Briefcase, Mail,
  Calendar, Activity, Download, TrendingUp, TrendingDown, ArrowUpRight,
  ArrowDownRight
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function UserAnalyticsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [chartView, setChartView] = useState("daily"); // daily | weekly | monthly
  const dailyChartRef = useRef(null);
  const weeklyChartRef = useRef(null);
  const monthlyChartRef = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get(`${API}/analytics/user`);
        setData(res.data);
      } catch { /* auth redirect */ }
      finally { setLoading(false); }
    };
    fetchData();
  }, []);

  // Draw daily chart
  useEffect(() => {
    if (!data || chartView !== "daily") return;
    const canvas = dailyChartRef.current;
    if (!canvas) return;
    drawBarChart(canvas, data.daily_activity, "count", "#6366f1", "date");
  }, [data, chartView]);

  // Draw weekly chart
  useEffect(() => {
    if (!data || chartView !== "weekly") return;
    const canvas = weeklyChartRef.current;
    if (!canvas) return;
    drawBarChart(canvas, data.weekly_activity, "count", "#3b82f6", "week");
  }, [data, chartView]);

  // Draw monthly chart
  useEffect(() => {
    if (!data || chartView !== "monthly") return;
    const canvas = monthlyChartRef.current;
    if (!canvas) return;
    drawBarChart(canvas, data.monthly_activity, "count", "#8b5cf6", "month");
  }, [data, chartView]);

  if (loading) {
    return <div className="analytics-loading" data-testid="analytics-loading"><div className="processing-spinner" /></div>;
  }

  if (!data) {
    return <div className="analytics-loading">Failed to load analytics</div>;
  }

  const planLabel = data.plan === "trial" ? "Free Trial" : data.plan === "base" ? "Base" : data.plan === "unlimited" ? "Unlimited" : data.plan;
  const mc = data.month_comparison || {};
  const isUp = mc.change_pct >= 0;

  return (
    <div className="page-container fade-in" data-testid="user-analytics-page">
      <div className="page-header">
        <div>
          <h1 data-testid="analytics-title">Your Analytics</h1>
          <p className="page-subtitle">Track your resume optimization activity and performance.</p>
        </div>
      </div>

      {/* Month-over-Month Banner */}
      {mc.prev_count !== undefined && (
        <div className={`analytics-mom-banner ${isUp ? "positive" : "negative"}`} data-testid="month-comparison">
          <div className="analytics-mom-icon">
            {isUp ? <ArrowUpRight size={18} /> : <ArrowDownRight size={18} />}
          </div>
          <div className="analytics-mom-text">
            <span className="analytics-mom-change">
              {isUp ? "+" : ""}{mc.change_pct}% vs last month
            </span>
            <span className="analytics-mom-detail">
              {mc.current_count} optimizations this month vs {mc.prev_count} last month
            </span>
          </div>
        </div>
      )}

      {/* Primary Stats Cards */}
      <div className="analytics-stats-grid" data-testid="analytics-stats">
        <div className="analytics-card analytics-card-primary" data-testid="stat-total-opts">
          <div className="analytics-card-icon"><BarChart3 size={20} /></div>
          <div className="analytics-card-body">
            <span className="analytics-card-label">Resumes Optimized</span>
            <span className="analytics-card-value">{data.total_optimizations}</span>
          </div>
        </div>

        <div className="analytics-card" data-testid="stat-opt-remaining">
          <div className="analytics-card-icon"><Target size={20} /></div>
          <div className="analytics-card-body">
            <span className="analytics-card-label">Optimizations Remaining</span>
            <span className="analytics-card-value">
              {data.opt_remaining == null ? "Unlimited" : data.opt_remaining}
            </span>
            {data.opt_remaining != null && (
              <div className="analytics-mini-bar">
                <div className="analytics-mini-bar-fill" style={{
                  width: `${Math.max(5, (data.opt_remaining / (data.opt_limit + data.opt_addon || 1)) * 100)}%`
                }} />
              </div>
            )}
          </div>
        </div>

        <div className="analytics-card" data-testid="stat-scans-remaining">
          <div className="analytics-card-icon"><Target size={20} /></div>
          <div className="analytics-card-body">
            <span className="analytics-card-label">Scans Remaining</span>
            <span className="analytics-card-value">
              {data.scan_remaining == null ? "Unlimited" : data.scan_remaining}
            </span>
            {data.scan_remaining != null && (
              <div className="analytics-mini-bar">
                <div className="analytics-mini-bar-fill" style={{
                  width: `${Math.max(5, (data.scan_remaining / (data.scan_limit + data.scan_addon || 1)) * 100)}%`
                }} />
              </div>
            )}
          </div>
        </div>

        <div className="analytics-card" data-testid="stat-applications">
          <div className="analytics-card-icon"><Briefcase size={20} /></div>
          <div className="analytics-card-body">
            <span className="analytics-card-label">Jobs Applied</span>
            <span className="analytics-card-value">{data.total_applications}</span>
          </div>
        </div>

        <div className="analytics-card" data-testid="stat-plan">
          <div className="analytics-card-icon"><Zap size={20} /></div>
          <div className="analytics-card-body">
            <span className="analytics-card-label">Current Plan</span>
            <span className="analytics-card-value analytics-plan-value">{planLabel}</span>
          </div>
        </div>
      </div>

      {/* Secondary Metric Cards */}
      <div className="analytics-secondary-grid" data-testid="analytics-secondary">
        <div className="analytics-mini-stat" data-testid="stat-emails">
          <Mail size={16} />
          <span className="analytics-mini-label">Emails Generated</span>
          <span className="analytics-mini-value">{data.total_emails}</span>
        </div>
        <div className="analytics-mini-stat" data-testid="stat-cover-letters">
          <FileText size={16} />
          <span className="analytics-mini-label">Cover Letters</span>
          <span className="analytics-mini-value">{data.total_cover_letters}</span>
        </div>
        <div className="analytics-mini-stat" data-testid="stat-downloads">
          <Download size={16} />
          <span className="analytics-mini-label">Downloads</span>
          <span className="analytics-mini-value">{data.total_downloads}</span>
        </div>
        <div className="analytics-mini-stat" data-testid="stat-this-month">
          <Activity size={16} />
          <span className="analytics-mini-label">This Month</span>
          <span className="analytics-mini-value">{data.optimizations_this_month}</span>
        </div>
      </div>

      {/* Applications Stats Row */}
      <div className="analytics-apps-row" data-testid="applications-stats">
        <div className="analytics-apps-card" data-testid="apps-today">
          <Calendar size={16} />
          <div>
            <div className="analytics-apps-value">{data.applications_today}</div>
            <div className="analytics-apps-label">Applications Today</div>
          </div>
        </div>
        <div className="analytics-apps-card" data-testid="apps-week">
          <Calendar size={16} />
          <div>
            <div className="analytics-apps-value">{data.applications_this_week}</div>
            <div className="analytics-apps-label">Applications This Week</div>
          </div>
        </div>
        <div className="analytics-apps-card" data-testid="apps-month">
          <Calendar size={16} />
          <div>
            <div className="analytics-apps-value">{data.applications_this_month}</div>
            <div className="analytics-apps-label">Applications This Month</div>
          </div>
        </div>
      </div>

      {/* Activity Chart */}
      <div className="analytics-chart-section" data-testid="activity-chart-section">
        <div className="analytics-chart-header">
          <h2><BarChart3 size={18} /> Optimization Activity</h2>
          <div className="analytics-toggle" data-testid="chart-view-toggle">
            <button
              className={`analytics-toggle-btn ${chartView === "daily" ? "active" : ""}`}
              onClick={() => setChartView("daily")}
              data-testid="toggle-daily"
            >Daily</button>
            <button
              className={`analytics-toggle-btn ${chartView === "weekly" ? "active" : ""}`}
              onClick={() => setChartView("weekly")}
              data-testid="toggle-weekly"
            >Weekly</button>
            <button
              className={`analytics-toggle-btn ${chartView === "monthly" ? "active" : ""}`}
              onClick={() => setChartView("monthly")}
              data-testid="toggle-monthly"
            >Monthly</button>
          </div>
        </div>
        <div className="analytics-chart-wrap" data-testid="activity-chart">
          {chartView === "daily" && <canvas ref={dailyChartRef} className="analytics-canvas" />}
          {chartView === "weekly" && <canvas ref={weeklyChartRef} className="analytics-canvas" />}
          {chartView === "monthly" && <canvas ref={monthlyChartRef} className="analytics-canvas" />}
        </div>
      </div>
    </div>
  );
}

// ── Canvas chart helper ──
function drawBarChart(canvas, data, valueKey, color, labelKey) {
  const ctx = canvas.getContext("2d");
  const dpr = window.devicePixelRatio || 1;
  const rect = canvas.getBoundingClientRect();
  canvas.width = rect.width * dpr;
  canvas.height = rect.height * dpr;
  ctx.scale(dpr, dpr);
  const w = rect.width;
  const h = rect.height;
  const pad = { top: 16, right: 12, bottom: 36, left: 40 };
  const chartW = w - pad.left - pad.right;
  const chartH = h - pad.top - pad.bottom;

  ctx.clearRect(0, 0, w, h);

  if (!data || data.length === 0) return;

  const values = data.map(d => d[valueKey] || 0);
  const maxVal = Math.max(...values, 1);

  const isDark = getComputedStyle(document.documentElement).getPropertyValue("--bg-deep").trim().startsWith("#0");
  const gridColor = isDark ? "rgba(148,163,184,0.08)" : "rgba(15,23,42,0.06)";
  const textColor = isDark ? "#64748b" : "#94a3b8";

  // Y axis gridlines
  const ySteps = 4;
  ctx.font = "10px Inter, sans-serif";
  ctx.textAlign = "right";

  for (let i = 0; i <= ySteps; i++) {
    const val = Math.round((maxVal / ySteps) * i);
    const y = pad.top + chartH - (val / maxVal) * chartH;
    ctx.fillStyle = textColor;
    ctx.fillText(val.toString(), pad.left - 6, y + 3);
    ctx.strokeStyle = gridColor;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(pad.left, y);
    ctx.lineTo(w - pad.right, y);
    ctx.stroke();
  }

  // Bars
  const barGap = Math.max(1, Math.floor(chartW / data.length * 0.2));
  const barW = Math.max(2, (chartW / data.length) - barGap);

  data.forEach((d, i) => {
    const val = d[valueKey] || 0;
    const barH = maxVal > 0 ? (val / maxVal) * chartH : 0;
    const x = pad.left + i * (barW + barGap) + barGap / 2;
    const y = pad.top + chartH - barH;

    const radius = Math.min(3, barW / 2);
    ctx.beginPath();
    ctx.moveTo(x, pad.top + chartH);
    ctx.lineTo(x, y + radius);
    ctx.quadraticCurveTo(x, y, x + radius, y);
    ctx.lineTo(x + barW - radius, y);
    ctx.quadraticCurveTo(x + barW, y, x + barW, y + radius);
    ctx.lineTo(x + barW, pad.top + chartH);
    ctx.closePath();

    ctx.fillStyle = val > 0 ? color : "rgba(148,163,184,0.1)";
    ctx.fill();

    const showEvery = Math.ceil(data.length / 8);
    if (i % showEvery === 0 || i === data.length - 1) {
      ctx.fillStyle = textColor;
      ctx.textAlign = "center";
      ctx.font = "9px Inter, sans-serif";
      const label = d[labelKey] || "";
      const displayLabel = label.length > 5 ? label.slice(5) : label;
      ctx.fillText(displayLabel, x + barW / 2, h - 8);
    }
  });
}
